import lo from 'lodash';
import modelExtend from 'dva-model-extend';
import { isEmptyObj } from 'utils/misc';

const model = {
  reducers: {
    updateState(state, { payload, prop }) {
      if (!prop) {
        return {
          ...state,
          ...payload,
        };
      }
      return {
        ...state,
        [prop]: {
          ...state[prop],
          ...payload,
        },
      };
    },
  },
};

const createAdvanceStaticModel = ({ namespace, apis, singleAttr, listAttr, pathname, processDataItem }) => modelExtend(model, {
  namespace,

  state: {
    ids: [],
    allIds: [],
    store: {},
    queryParams: {},
    lastCreatedItem: {},
  },

  subscriptions: {
    setup({ dispatch, history }) {
      history.listen((location) => {
        if (location.pathname === pathname) {
          dispatch({
            type: 'query',
            payload: location.query,
          });
        }
      });
    },
  },

  effects: {
    * reload(_, { select, put }) {
      const { queryParams } = yield (select(state => state[namespace]));
      yield put({ type: 'query', payload: queryParams });
    },

    * init(_, { put }) {
      yield put({ type: 'query', payload: false });
    },

    * query({ payload, output }, { call, put }) {
      const data = yield call(apis.query, payload);
      output && (output.value = data);
      if (data.success) {
        const result = data[listAttr];
        if (result && processDataItem) {
          result.forEach(processDataItem);
        }
        yield put({
          type: 'querySuccess',
          payload: { result, queryParams: payload },
        });
      } else {
        throw data;
      }
    },

    * delete({ payload, output }, { call, put }) {
      const data = yield call(apis.remove, { id: payload });
      output && (output.value = data);
      if (data.success) {
        yield put({ type: 'deleteSuccess', payload });
      } else {
        throw data;
      }
    },

    * deleteMany({ payload, output }, { call, put }) {
      const data = yield call(apis.removeMany, { ids: payload });
      output && (output.value = data);
      if (data.success) {
        yield put({ type: 'query' });
      } else {
        throw data;
      }
    },

    * create({ payload, output }, { call, put }) {
      const data = yield call(apis.create, payload);
      output && (output.value = data);
      if (data.success) {
        const result = data[singleAttr];
        if (result && processDataItem) {
          processDataItem(result);
        }
        yield put({ type: 'createSuccess', payload: result });
      } else {
        throw data;
      }
    },

    * createMany({ payload, output }, { call, put }) {
      const data = yield call(apis.createMany, payload);
      output && (output.value = data);
      if (data.success) {
        yield put({ type: 'query' });
      } else {
        throw data;
      }
    },

    * update({ payload, output }, { call, put }) {
      const data = yield call(apis.update, payload);
      output && (output.value = data);
      if (data.success) {
        const result = data[singleAttr];
        if (result && processDataItem) {
          processDataItem(result);
        }
        yield put({ type: 'updateSuccess', payload: result });
      } else {
        throw data;
      }
    },

  },

  reducers: {
    deleteSuccess(state, { payload: id }) {
      const ids = state.ids.filter(o => o !== id);
      const allIds = state.allIds.filter(o => o !== id);
      const store = { ...state.store };
      if (id in store) {
        delete store[id];
      }
      return { ...state, ids, allIds, store };
    },
    createSuccess(state, { payload }) {
      const newObj = payload;
      const ids = [newObj.id, ...state.ids];
      const allIds = [newObj.id, ...state.allIds];
      const store = { ...state.store, [newObj.id]: newObj };
      return { ...state, ids, allIds, store, lastCreatedItem: newObj };
    },
    updateSuccess(state, { payload }) {
      const updatedObj = payload;
      const store = { ...state.store, [updatedObj.id]: updatedObj };
      return { ...state, store };
    },
    querySuccess(state, { payload: { result, queryParams } }) {
      const list = result;
      const store = {};
      const ids = [];
      list.forEach((item) => {
        store[item.id] = item;
        ids.push(item.id);
      });

      // queryParams === false if initial request
      if ((queryParams === false && !isEmptyObj(state.queryParams))) {
        return { ...state, store, allIds: ids };
      }

      if (isEmptyObj(queryParams)) {
        return { ...state, store, ids, allIds: ids, queryParams: null };
      }

      return { ...state, ids, queryParams, store: { ...state.store, ...store } };
    },
    updateItems(state, { payload, merge = true }) {
      const store = { ...state.store };
      Object.keys(payload).forEach((id) => {
        const updatedObj = payload[id];
        const currentItem = store[id] || {};
        store[id] = merge ? lo.merge(currentItem, updatedObj) : { ...currentItem, updatedObj };
      });

      return { ...state, store };
    },
    select(state, { payload: selectedId }) {
      const allIds = state.allIds.filter(id => id !== selectedId);
      allIds.unshift(selectedId);
      return { ...state, allIds };
    },
  },

});

const createBasicStaticModel = ({ namespace, apis, singleAttr, listAttr }) => modelExtend(model, {
  namespace,

  state: {
    ids: [],
    store: {},
    lastCreatedItem: {},
  },

  effects: {
    * query({ payload = {}, output }, { call, put }) {
      const data = yield call(apis.query, payload);
      output && (output.value = data);
      if (data.success) {
        const list = data[listAttr];
        const store = {};
        const ids = [];
        list.forEach((item) => {
          store[item.id] = item;
          ids.push(item.id);
        });
        yield put({
          type: 'updateState',
          payload: { ids, store },
        });
      } else {
        throw data;
      }
    },

    * delete({ payload, output }, { call, put }) {
      const data = yield call(apis.remove, { id: payload });
      output && (output.value = data);
      if (data.success) {
        yield put({ type: 'query' });
      } else {
        throw data;
      }
    },

    * create({ payload, output }, { call, put }) {
      const data = yield call(apis.create, payload);
      output && (output.value = data);
      if (data.success) {
        yield put({
          type: 'updateState',
          payload: {
            lastCreatedItem: data[singleAttr],
          },
        });
        yield put({ type: 'query' });
      } else {
        throw data;
      }
    },

    * update({ payload, output }, { call, put }) {
      const data = yield call(apis.update, payload);
      output && (output.value = data);
      if (data.success) {
        yield put({ type: 'query' });
      } else {
        throw data;
      }
    },

  },
});

const pageModel = modelExtend(model, {

  state: {
    list: [],
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: total => `Total ${total} Items`,
      current: 1,
      total: 0,
    },
  },

  reducers: {
    querySuccess(state, { payload }) {
      const { list, pagination } = payload;
      return {
        ...state,
        list,
        pagination: {
          ...state.pagination,
          ...pagination,
        },
      };
    },
  },

});


export {
  model,
  pageModel,
  createAdvanceStaticModel,
  createBasicStaticModel,
};



// WEBPACK FOOTER //
// ./src/models/common.js